// "Log in with Telegram" — OpenID Connect flow (https://oauth.telegram.org/).
// Distinct from the classic telegram-widget.js script: this is a full
// authorization-code + PKCE redirect flow registered with exact
// Redirect URIs / Trusted Origins in Telegram's OAuth app settings.

const STATE_KEY    = 'tg_oidc_state'
const VERIFIER_KEY = 'tg_oidc_verifier'
const AUTH_URL     = 'https://oauth.telegram.org/auth'

function base64url(bytes) {
  let str = ''
  bytes.forEach(b => { str += String.fromCharCode(b) })
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

function randomString(byteLength = 48) {
  const arr = new Uint8Array(byteLength)
  crypto.getRandomValues(arr)
  return base64url(arr)
}

async function sha256base64url(input) {
  const data = new TextEncoder().encode(input)
  const digest = await crypto.subtle.digest('SHA-256', data)
  return base64url(new Uint8Array(digest))
}

// Redirects the browser to Telegram's OAuth consent screen.
export async function startTelegramOidcLogin(clientId, redirectUri) {
  const state    = randomString(32)
  const verifier = randomString(64)
  const challenge = await sha256base64url(verifier)

  sessionStorage.setItem(STATE_KEY, state)
  sessionStorage.setItem(VERIFIER_KEY, verifier)

  const params = new URLSearchParams({
    client_id: clientId,
    redirect_uri: redirectUri,
    response_type: 'code',
    scope: 'openid',
    state,
    code_challenge: challenge,
    code_challenge_method: 'S256',
  })
  window.location.href = `${AUTH_URL}?${params.toString()}`
}

// Call on app load. If the URL carries a valid `code`+`state` pair matching
// what we stashed before redirecting, returns { code, verifier } and cleans
// the query string; otherwise returns null (nothing to do).
export function consumeTelegramOidcCallback() {
  const params = new URLSearchParams(window.location.search)
  const code  = params.get('code')
  const state = params.get('state')
  if (!code || !state) return null

  const storedState = sessionStorage.getItem(STATE_KEY)
  const verifier     = sessionStorage.getItem(VERIFIER_KEY)
  sessionStorage.removeItem(STATE_KEY)
  sessionStorage.removeItem(VERIFIER_KEY)

  // Strip code/state from the address bar so a page refresh doesn't resubmit.
  window.history.replaceState({}, '', window.location.pathname)

  if (!storedState || state !== storedState || !verifier) return null
  return { code, verifier }
}

// True as soon as the module loads if the current URL looks like a Telegram
// OIDC callback — lets callers decide whether to show a "processing" screen
// before running the async exchange.
export function hasTelegramOidcCallback() {
  const params = new URLSearchParams(window.location.search)
  return Boolean(params.get('code') && params.get('state'))
}
